/**
 * TTS Manager - Extracted from Phantasy AudioService
 * Handles multiple TTS providers with fallbacks and audio analysis
 */

import { SDKEventEmitter } from "../core/EventEmitter";
import { AlkahestProvider } from "./providers/AlkahestProvider";
import { ElevenLabsProvider } from "./providers/ElevenLabsProvider";
import { GPTSoVITSProvider } from "./providers/GPTSoVITSProvider";
import { AudioAnalyzer } from "./AudioAnalyzer";
import { createLogger } from "../utils/logger";

import type { TTSConfig, TTSProviderConfig } from "../types/config";
import type { ITTSProvider } from "./providers/ITTSProvider";

type AudioDataCallback = (volume: number) => void;

export class TTSManager extends SDKEventEmitter {
  private logger: ReturnType<typeof createLogger>;
  private config: TTSConfig;
  private primaryProvider: ITTSProvider;
  private fallbackProvider?: ITTSProvider;
  private audioAnalyzer: AudioAnalyzer;
  private audioDataCallback?: AudioDataCallback;
  private _isReady = false;

  constructor(config: TTSConfig) {
    super();
    this.logger = createLogger("TTSManager");
    this.config = config;

    this.primaryProvider = this.createProvider(config.primary);

    if (config.fallback) {
      this.fallbackProvider = this.createProvider(config.fallback);
    }

    this.audioAnalyzer = new AudioAnalyzer();
    this.setupAudioAnalyzer();
  }

  async initialize(): Promise<void> {
    try {
      this.logger.info("Initializing TTS Manager...");

      await this.audioAnalyzer.initialize();
      this._isReady = true;

      this.logger.info("TTS Manager initialized successfully");
    } catch (error) {
      this.logger.warn(
        "TTS Manager AudioAnalyzer had issues, but continuing initialization:",
        error,
      );
      this._isReady = true;
      this.logger.info(
        "TTS Manager initialized (AudioContext may need user gesture)",
      );
    }
  }

  /**
   * Generate audio from text using configured provider
   */
  async generateAudio(text: string): Promise<Blob> {
    this.ensureReady();

    try {
      this.logger.info(
        "Generating audio with primary provider:",
        this.config.primary.provider,
      );
      return await this.primaryProvider.generateAudio(text);
    } catch (primaryError) {
      this.logger.warn("Primary provider failed:", primaryError);

      if (this.fallbackProvider) {
        try {
          this.logger.info(
            "Attempting fallback provider:",
            this.config.fallback?.provider,
          );
          return await this.fallbackProvider.generateAudio(text);
        } catch (fallbackError) {
          this.logger.error("Both primary and fallback providers failed");
          this.logger.error("Primary error:", primaryError);
          this.logger.error("Fallback error:", fallbackError);
          const primaryMessage =
            primaryError instanceof Error
              ? primaryError.message
              : String(primaryError);
          throw new Error(
            `TTS generation failed with both providers: ${primaryMessage}`,
          );
        }
      } else {
        throw primaryError;
      }
    }
  }

  /**
   * Play audio blob with optional analysis for lip sync
   */
  async playAudio(audioBlob: Blob): Promise<void> {
    this.ensureReady();

    try {
      await this.audioAnalyzer.playWithAnalysis(
        audioBlob,
        this.audioDataCallback,
      );
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error("Audio playback failed:", err);
      this.emit("error", err);
      throw err;
    }
  }

  /**
   * Set audio data callback for lip sync
   */
  setAudioDataCallback(callback: AudioDataCallback): void {
    this.audioDataCallback = callback;
    this.logger.debug("Audio data callback set");
  }

  /**
   * Stop current audio playback
   */
  stop(): void {
    if (this.isReady()) {
      this.audioAnalyzer.stop();
    }
  }

  /**
   * Update TTS configuration
   */
  updateConfig(newConfig: TTSConfig): void {
    this.logger.info("Updating TTS configuration");

    // Check if providers need to be recreated
    if (newConfig.primary.provider !== this.config.primary.provider) {
      this.primaryProvider = this.createProvider(newConfig.primary);
    }

    if (newConfig.fallback) {
      if (
        !this.config.fallback ||
        newConfig.fallback.provider !== this.config.fallback.provider
      ) {
        this.fallbackProvider = this.createProvider(newConfig.fallback);
      }
    }

    this.config = newConfig;
  }

  /**
   * Check if TTS manager is ready
   */
  isReady(): boolean {
    return this._isReady;
  }

  /**
   * Get current configuration
   */
  getConfig(): TTSConfig {
    return { ...this.config };
  }

  /**
   * Destroy and clean up resources
   */
  destroy(): void {
    this.logger.info("Destroying TTS Manager...");

    try {
      this.stop();
      this.audioAnalyzer.destroy();
      this.removeAllListeners();
      this._isReady = false;

      this.logger.info("TTS Manager destroyed");
    } catch (error) {
      this.logger.error("Error during TTS Manager destruction:", error);
    }
  }

  // Private methods

  private createProvider(config: TTSProviderConfig): ITTSProvider {
    switch (config.provider) {
      case "alkahest":
        return new AlkahestProvider(config);
      case "elevenlabs":
        return new ElevenLabsProvider(config);
      case "gptsovits":
        return new GPTSoVITSProvider(config);
      default:
        throw new Error(`Unknown TTS provider: ${config.provider}`);
    }
  }

  private setupAudioAnalyzer(): void {
    this.audioAnalyzer.on("error", (error) => {
      this.logger.error("Audio analyzer error:", error);
      this.emit("error", error);
    });

    this.audioAnalyzer.on("playbackStarted", () => {
      this.logger.debug("Audio playback started");
    });

    this.audioAnalyzer.on("playbackEnded", () => {
      this.logger.debug("Audio playback ended");
      // Reset lip sync to neutral
      if (this.audioDataCallback) {
        this.audioDataCallback(0);
      }
    });
  }

  private ensureReady(): void {
    if (!this._isReady) {
      throw new Error("TTS Manager not initialized. Call initialize() first.");
    }
  }
}
